import { captureError } from '@smg-real-estate/browser-rum';
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { nextTick } from 'vue';
import { createI18n, type I18n } from 'vue-i18n';

export const DEFAULT_LANGUAGE = HgSupportedLanguage.DE;
export enum LocaleFileSuffix {
  HG = 'hg',
  IS24 = 'is24',
}

let localeSuffix: LocaleFileSuffix;

let i18n: I18n<
  Record<string, unknown>,
  Record<string, unknown>,
  Record<string, unknown>,
  string,
  false
>;

export async function initI18n(
  fileSuffix: LocaleFileSuffix = LocaleFileSuffix.HG,
  currentLanguage: HgSupportedLanguage = DEFAULT_LANGUAGE,
): Promise<
  I18n<Record<string, unknown>, Record<string, unknown>, Record<string, unknown>, string, false>
> {
  localeSuffix = fileSuffix;

  // load translations
  const translations = await import(`@/i18n/${currentLanguage}-${localeSuffix}.ts`);
  const messages = { [currentLanguage]: { ...translations.default } };
  if (currentLanguage !== DEFAULT_LANGUAGE) {
    const defaultTranslations = await import(`@/i18n/${DEFAULT_LANGUAGE}-${localeSuffix}.ts`);
    messages[DEFAULT_LANGUAGE] = { ...defaultTranslations.default };
  }

  i18n = createI18n<false>({
    legacy: false,
    locale: currentLanguage,
    fallbackLocale: DEFAULT_LANGUAGE,
    silentFallbackWarn: true,
    missing: (currentLocale, key) =>
      captureError(new Error('Missing translation key'), { locale: currentLocale, key }),
    messages,
  });
  return i18n;
}

export async function safeLoadTranslations(locale: HgSupportedLanguage): Promise<void> {
  try {
    await loadTranslations(locale);
  } catch (error) {
    captureError(error, { service: 'i18n', action: 'load-translations', locale });
  }
}

export async function loadTranslations(locale: HgSupportedLanguage): Promise<void> {
  const localeLoaded = await import(`@/i18n/${locale}-${localeSuffix}.ts`);
  i18n.global.mergeLocaleMessage(locale, { ...localeLoaded.default });
  return nextTick();
}

export async function setLocaleBeforeRouteChange(locale: HgSupportedLanguage): Promise<void> {
  if (!(locale in (i18n.global.messages.value as Record<string, any>))) {
    await safeLoadTranslations(locale);
  }
}
